/*
 * Markdown as you type (F-2 §9): a marker at the start of a line makes the block it names, the
 * closing marker of an inline span applies the mark, and a shortcode the emoji table knows becomes
 * its character. Every rule leaves through `undoInputRule`, so Backspace right after one restores
 * the typed characters — `1. ` can be typed literally. Nothing fires inside a code block (the
 * plugin's own rule) or inside a code span (this file's guard), and `---` is deliberately not a
 * rule: a person typing a dash line and a rule are too close.
 *
 * A *line* start is not only a block's start. Shift+Enter puts a hard break in the paragraph, and a
 * marker typed after it names the same block a marker at the very start does (X-A1 D9 a; Slack
 * converts at any line start). Every block marker therefore carries two rules — the plugin's own,
 * which matches from the start of the textblock, and the one beside it, which matches right after a
 * break, drops the break and splits the paragraph there — both written from one table of markers,
 * so a marker cannot come to mean two things depending on the line it is typed on.
 */

import { setBlockType, wrapIn } from "prosemirror-commands"
import {
	InputRule,
	inputRules,
	textblockTypeInputRule,
	wrappingInputRule,
} from "prosemirror-inputrules"
import type { MarkType } from "prosemirror-model"
import { wrapInList } from "prosemirror-schema-list"
import type { Command, EditorState, Plugin, Transaction } from "prosemirror-state"
import { inOneStep, openLineAt, type EditorKit } from "./kit.ts"

/**
 * What the shortcode of an emoji rule becomes. The table is the composer's, loaded with the emoji
 * list, so the surface that has one hands the lookup in and a surface without one leaves it out:
 * nothing in this tree reaches for a table that may not be there.
 */
export interface InputRuleDeps {
	/** The character `:tada:` stands for, or null while the table is absent or does not know it. */
	emoji?: (shortcode: string) => string | null
}

/**
 * The block markers, as the source of both rule sets. `\s` before a marker is the indentation
 * markdown allows; the capture groups are what the block's attributes are read from. The fence's
 * info string is the dialect's, so a rule can never open a code block the serializer cannot write.
 */
function markersOf(kit: EditorKit) {
	return {
		quote: "\\s*>\\s",
		bullet: "\\s*[-*+]\\s",
		ordered: "(\\d+)\\.\\s",
		heading: "(#{1,6})\\s",
		fence: `\`\`\`(${kit.dialect.fenceInfo})\\s`,
	} as const
}

type MarkerKind = keyof ReturnType<typeof markersOf>

/** What the marker makes of the block it opens; the match is the rule's own. */
function blocksOf(kit: EditorKit): Record<MarkerKind, (match: RegExpMatchArray) => Command> {
	const { N, lists, listAttrs } = kit
	return {
		quote: () => wrapIn(N.blockquote),
		bullet: () => wrapInList(lists.bullet.type, listAttrs("bullet")),
		ordered: (match) =>
			wrapInList(lists.ordered.type, listAttrs("ordered", { start: Number(match[1]) })),
		heading: (match) => setBlockType(N.heading, kit.headingAttrs((match[1] ?? "#").length)),
		fence: (match) => setBlockType(N.codeBlock, kit.codeBlockAttrs(match[1] ?? "")),
	}
}

const atBlockStart = (marker: string) => new RegExp(`^${marker}$`)

/**
 * `textBetween` writes one `￼` where a leaf node stands, and a hard break is the only leaf a
 * paragraph of this dialect can hold before the caret — an image is a chip and carries no text, so
 * the rule still checks the node it found before it acts.
 */
const afterLineBreak = (marker: string) => new RegExp(`\\ufffc${marker}$`)

function inCodeMark(kit: EditorKit, state: EditorState, pos: number): boolean {
	return kit.M.code.isInSet(state.doc.resolve(pos).marks()) !== undefined
}

/**
 * An inline span: the pattern's first group is what stands before the opening marker (kept), the
 * second the text between the markers. The markers go, the text stays, the mark is applied and
 * then not stored, so the next character typed is plain again.
 */
function markRule(kit: EditorKit, pattern: RegExp, mark: MarkType): InputRule {
	return new InputRule(
		pattern,
		(state, match, start, end) => {
			const lead = match[1] ?? ""
			const text = match[2] ?? ""
			const from = start + lead.length
			if (text === "" || inCodeMark(kit, state, from)) return null
			return state.tr
				.delete(from, end)
				.insertText(text, from)
				.addMark(from, from + text.length, mark.create())
				.removeStoredMark(mark)
		},
		{ inCodeMark: false },
	)
}

/** `[ ] ` or `[x] ` at the start of a list item marks the item, as the server reads it. */
function taskRule({ N, checkedAttrs }: EditorKit): InputRule {
	return new InputRule(/^\[( |x|X)\]\s$/, (state, match, start, end) => {
		const { $from } = state.selection
		if ($from.parentOffset !== end - start || $from.depth < 2) return null
		if ($from.node(-1).type !== N.listItem || $from.index(-1) !== 0) return null
		const item = $from.before(-1)
		return state.tr.delete(start, end).setNodeMarkup(item, undefined, {
			...$from.node(-1).attrs,
			...checkedAttrs(match[1] !== " "),
		})
	})
}

/**
 * `:tada:` becomes 🎉 the moment the closing colon is typed — only when the table is already here
 * (the list's first query loads it) and knows the code; anything else stays text for the server.
 */
function emojiRule(kit: EditorKit, emoji: (shortcode: string) => string | null): InputRule {
	return new InputRule(
		/(^|\s):([a-z0-9_+-]{2,}):$/,
		(state, match, start, end) => {
			const character = emoji(match[2] ?? "")
			const from = start + (match[1] ?? "").length
			if (character === null || inCodeMark(kit, state, from)) return null
			return state.tr.insertText(character, from, end)
		},
		{ inCodeMark: false },
	)
}

/**
 * The marker typed on a second line: the break and the marker go, the paragraph splits where the
 * break stood, and the block is made over the part that follows — one step, so Backspace takes the
 * whole thing back. The markdown this leaves is the markdown the same text pasted would have
 * produced: `a\n\n- b`.
 */
function afterBreakRule(
	kit: EditorKit,
	marker: string,
	block: (match: RegExpMatchArray) => Command,
): InputRule {
	return new InputRule(afterLineBreak(marker), (state, match, start, end) => {
		if (state.doc.nodeAt(start)?.type !== kit.N.hardBreak) return null
		const collected: { tr: Transaction | null } = { tr: null }
		inOneStep(openLineAt(start, end), block(match))(state, (tr) => {
			collected.tr = tr
		})
		return collected.tr
	})
}

export function buildInputRules(kit: EditorKit, deps: InputRuleDeps = {}): Plugin {
	const { N, M, lists, listKind, listAttrs } = kit
	const { orderedStart } = kit.roles.attrs
	const markers = markersOf(kit)
	const blocks = blocksOf(kit)
	const emoji = deps.emoji
	return inputRules({
		rules: [
			wrappingInputRule(atBlockStart(markers.quote), N.blockquote),
			// A marker joins the list right above it only when that list is of its own kind: where
			// the kinds share one node, the type alone would join a bullet onto a numbered list.
			wrappingInputRule(
				atBlockStart(markers.bullet),
				lists.bullet.type,
				listAttrs("bullet"),
				(_match, node) => listKind(node) === "bullet",
			),
			wrappingInputRule(
				atBlockStart(markers.ordered),
				lists.ordered.type,
				(match) => listAttrs("ordered", { start: Number(match[1]) }),
				(match, node) =>
					listKind(node) === "ordered" &&
					node.childCount + (node.attrs[orderedStart] as number) === Number(match[1]),
			),
			textblockTypeInputRule(atBlockStart(markers.heading), N.heading, (match) =>
				kit.headingAttrs((match[1] ?? "#").length),
			),
			textblockTypeInputRule(atBlockStart(markers.fence), N.codeBlock, (match) =>
				kit.codeBlockAttrs(match[1] ?? ""),
			),
			...(Object.keys(markers) as MarkerKind[]).map((kind) =>
				afterBreakRule(kit, markers[kind], blocks[kind]),
			),
			taskRule(kit),
			markRule(kit, /(^|[^*])\*\*([^*\s](?:[^*]*[^*\s])?)\*\*$/, M.bold),
			markRule(kit, /(^|[^*\w])\*([^*\s](?:[^*]*[^*\s])?)\*$/, M.italic),
			markRule(kit, /(^|[^\w_])_([^_\s](?:[^_]*[^_\s])?)_$/, M.italic),
			markRule(kit, /(^|[^~])~~([^~\s](?:[^~]*[^~\s])?)~~$/, M.strike),
			markRule(kit, /(^|[^`])`([^`\s](?:[^`]*[^`\s])?)`$/, M.code),
			...(emoji === undefined ? [] : [emojiRule(kit, emoji)]),
		],
	})
}
